import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { RsvpBuilder, withFieldIds, stripFieldIds } from './RsvpBuilder';
import { TimePicker } from './TimePicker';
import api from '../../lib/api';
import { Sparkles } from 'lucide-react';
import { FLYER_LAYOUTS } from '../flyer/FlyerCanvas';
import { FONT_THEMES, getFontTheme, ensureAllFontsLoaded } from '../../lib/fontThemes';
import { TIMEZONES, DEFAULT_TIMEZONE, formatTime12, zoneAbbr, formatDateLong } from '../../lib/time';

// Expanded flyer presets — legacy 5 + on-brand Thrive combos (theme / accent).
const COLORS = [
  { name: 'Sage & Clay', theme: '#2F6B57', accent: '#C86B4A' },
  { name: 'Ocean & Gold', theme: '#2E6F7E', accent: '#E0A458' },
  { name: 'Plum & Clay', theme: '#6B3F69', accent: '#C86B4A' },
  { name: 'Forest & Sand', theme: '#3A5A40', accent: '#A98467' },
  { name: 'Charcoal & Clay', theme: '#1F2937', accent: '#C86B4A' },
  { name: 'Thrive Teal & Coral', theme: '#2BB7C4', accent: '#F0593E' },
  { name: 'Thrive Teal & Royal', theme: '#2BB7C4', accent: '#2D6BE0' },
  { name: 'Thrive Royal & Teal', theme: '#2D6BE0', accent: '#2BB7C4' },
  { name: 'Navy & Teal Glow', theme: '#0B1220', accent: '#2BB7C4' },
  { name: 'Navy & Coral Pop', theme: '#0B1220', accent: '#F0593E' },
  { name: 'Deep Blue & Coral', theme: '#1E63D6', accent: '#F0593E' },
  { name: 'Ice Teal & Navy', theme: '#7FE3EA', accent: '#0B1220' },
  { name: 'Sea Glass & Apricot', theme: '#3CC7B8', accent: '#FF8A5B' },
  { name: 'Lagoon & Sunset', theme: '#1FA6B8', accent: '#FF6B4A' },
  { name: 'Harbor Blue & Citrus', theme: '#2A6FE3', accent: '#FFB24A' },
  { name: 'Midnight & Champagne', theme: '#111827', accent: '#E7CBA9' },
  { name: 'Ink & Seafoam', theme: '#0F172A', accent: '#7BE7D6' },
  { name: 'Spruce & Coral', theme: '#1F6F5B', accent: '#F0593E' },
];

const DEFAULT_FIELDS = [
  { key: 'guest_count', label: 'How many in your party? (including you)', type: 'number', required: false, options: [], share_responses: false },
  { key: 'bringing', label: 'What are you bringing?', type: 'text', required: false, options: [], share_responses: true },
  { key: 'message', label: 'Anything to share with the host?', type: 'textarea', required: false, options: [], share_responses: false },
];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Tiny visual representation of each flyer layout for the picker.
const LayoutThumb = ({ id, theme, accent }) => {
  const grad = `linear-gradient(135deg, ${theme}, ${accent})`;
  const bar = (w, c = '#cbd5e1') => <div className="rounded-sm" style={{ height: 3, width: w, background: c }} />;
  return (
    <div className="aspect-[4/3] w-full rounded-lg border border-border bg-white overflow-hidden flex flex-col" aria-hidden="true">
      {id === 'poster' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-1 p-2" style={{ background: grad }}>
          {bar(28, 'rgba(255,255,255,0.95)')}{bar(20, 'rgba(255,255,255,0.7)')}
        </div>
      )}
      {id === 'photo' && (<>
        <div className="h-[55%] flex items-end p-1.5" style={{ background: grad }}>{bar(22, 'rgba(255,255,255,0.95)')}</div>
        <div className="flex-1 p-1.5 flex flex-col gap-1">{bar('70%')}{bar('50%')}</div>
      </>)}
      {id === 'minimal' && (<>
        <div style={{ height: 4, background: accent }} />
        <div className="flex-1 p-2 flex flex-col gap-1.5">{bar('60%', theme)}{bar('40%')}{bar('50%')}</div>
      </>)}
      {id === 'festive' && (<>
        <div className="h-[45%] flex items-center justify-center p-1.5" style={{ background: grad }}>{bar(24, 'rgba(255,255,255,0.95)')}</div>
        <div className="flex-1 p-1.5 grid grid-cols-2 gap-1">
          <div className="rounded" style={{ background: `${theme}22` }} /><div className="rounded" style={{ background: `${accent}22` }} />
        </div>
      </>)}
      {id === 'editorial' && (
        <div className="flex-1 flex">
          <div style={{ width: 5, background: accent }} />
          <div className="flex-1 p-2 flex flex-col gap-1.5">{bar('65%', theme)}{bar('45%')}<div className="mt-1" style={{ height: 2, background: '#1f2937' }} />{bar('50%')}</div>
        </div>
      )}
      {(id === 'classic' || !['poster', 'photo', 'minimal', 'festive', 'editorial'].includes(id)) && (<>
        <div className="h-[40%] flex items-end p-1.5" style={{ background: grad }}>{bar(20, 'rgba(255,255,255,0.9)')}</div>
        <div className="flex-1 p-1.5 grid grid-cols-2 gap-1.5">
          <div className="flex flex-col gap-1">{bar('80%')}{bar('60%')}</div>
          <div className="flex flex-col gap-1">{bar('80%')}{bar('60%')}</div>
        </div>
      </>)}
    </div>
  );
};

export const EventForm = ({ initial, onSubmit, submitLabel = 'Create event', submitting = false }) => {
  const [form, setForm] = useState(() => ({
    title: initial?.title || '',
    host: initial?.host || '',
    date: initial?.date || '',
    time: initial?.time || '',
    end_time: initial?.end_time || '',
    timezone: initial?.timezone || DEFAULT_TIMEZONE,
    location_name: initial?.location_name || '',
    address: initial?.address || '',
    description: initial?.description || '',
    theme_color: initial?.theme_color || '#2F6B57',
    accent_color: initial?.accent_color || '#C86B4A',
    flyer_layout: initial?.flyer_layout || 'classic',
    font_theme: initial?.font_theme || 'editorial',
    cover_image: initial?.cover_image || '',
    logo_url: initial?.logo_url || '',
    rsvp_allow_edit: initial?.rsvp_allow_edit !== undefined ? initial.rsvp_allow_edit : true,
  }));
  const [fields, setFields] = useState(() => withFieldIds(initial?.rsvp_fields || DEFAULT_FIELDS));
  const [brandKits, setBrandKits] = useState([]);

  useEffect(() => {
    ensureAllFontsLoaded();
    api.get('/brand-kits').then((r) => setBrandKits(r.data)).catch(() => {});
  }, []);

  const applyKit = (kitId) => {
    const kit = brandKits.find((k) => k.id === kitId);
    if (!kit) return;
    setForm((f) => ({
      ...f,
      theme_color: kit.theme_color,
      accent_color: kit.accent_color,
      font_theme: kit.font_theme,
      flyer_layout: kit.flyer_layout,
      logo_url: kit.logo_url || '',
    }));
    toast.success(`Applied “${kit.name}”`);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Give your event a title'); return; }
    const cleanFields = stripFieldIds(fields).filter((f) => f.label.trim());
    onSubmit({ ...form, rsvp_fields: cleanFields });
  };

  const previewTime = formatTime12(form.time);
  const previewAbbr = zoneAbbr(form.timezone);

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl font-semibold mb-4">Event details</h2>
          {brandKits.length > 0 && (
            <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="apply-brand-kit">
              <div className="flex items-center gap-2 text-sm font-medium flex-shrink-0">
                <Sparkles className="h-4 w-4 text-primary" /> Apply a brand kit
              </div>
              <Select onValueChange={applyKit}>
                <SelectTrigger className="h-10 sm:max-w-xs" data-testid="apply-brand-kit-select">
                  <SelectValue placeholder="Choose a saved kit…" />
                </SelectTrigger>
                <SelectContent>
                  {brandKits.map((k) => (<SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Sets colors, font, layout &amp; logo.</span>
            </div>
          )}
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Event title *</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Summer Garden Party" className="h-11" data-testid="event-title-input" /></div>
            <div className="space-y-1.5"><Label>Hosted by</Label><Input value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="Your name" className="h-11" data-testid="event-host-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="h-11" data-testid="event-date-input" /></div>
              <div className="space-y-1.5">
                <Label>Time zone</Label>
                <Select value={form.timezone} onValueChange={(v) => set('timezone', v)}>
                  <SelectTrigger className="h-11" data-testid="event-timezone-select"><SelectValue placeholder="Select time zone" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {TIMEZONES.map((z) => <SelectItem key={z.iana} value={z.iana}>{z.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start time</Label><TimePicker value={form.time} onChange={(v) => set('time', v)} testid="event-time-input" /></div>
              <div className="space-y-1.5"><Label>End time</Label><TimePicker value={form.end_time} onChange={(v) => set('end_time', v)} testid="event-end-time-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Venue name</Label><Input value={form.location_name} onChange={(e) => set('location_name', e.target.value)} placeholder="The Rooftop Garden" className="h-11" data-testid="event-venue-input" /></div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="123 Garden Lane, Austin, TX" className="h-11" data-testid="event-address-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Tell your guests what to expect…" rows={4} data-testid="event-description-input" /></div>
            <div className="space-y-1.5"><Label>Cover image URL (optional)</Label><Input value={form.cover_image} onChange={(e) => set('cover_image', e.target.value)} placeholder="https://…" className="h-11" /></div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl font-semibold mb-1">Flyer layout</h2>
          <p className="text-sm text-muted-foreground mb-4">Choose a layout style for your flyer and invite email. Your colors below apply to whichever layout you pick.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="flyer-layout-grid">
            {FLYER_LAYOUTS.map((l) => {
              const selected = (form.flyer_layout || 'classic') === l.id;
              return (
                <button key={l.id} type="button" onClick={() => set('flyer_layout', l.id)}
                  className={`text-left rounded-xl border-2 p-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                  aria-pressed={selected} data-testid={`flyer-layout-${l.id}`}>
                  <LayoutThumb id={l.id} theme={form.theme_color} accent={form.accent_color} />
                  <div className="text-sm font-medium mt-2">{l.name}</div>
                  <div className="text-[11px] text-muted-foreground leading-tight">{l.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl font-semibold mb-1">Flyer theme</h2>
          <p className="text-sm text-muted-foreground mb-4">Pick a preset or set your own colors. This drives the flyer and invite email.</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5" data-testid="flyer-theme-preset-grid">
            {COLORS.map((c) => {
              const selected = form.theme_color.toLowerCase() === c.theme.toLowerCase() && form.accent_color.toLowerCase() === c.accent.toLowerCase();
              return (
                <button key={c.name} type="button" onClick={() => { set('theme_color', c.theme); set('accent_color', c.accent); }}
                  className={`group rounded-xl p-1 border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'border-primary' : 'border-transparent hover:border-border'}`}
                  title={c.name} data-testid="flyer-theme-preset-swatch" aria-pressed={selected}>
                  <div className="h-12 w-full rounded-lg" style={{ background: `linear-gradient(135deg, ${c.theme}, ${c.accent})` }} />
                  <div className="text-[10px] leading-tight text-muted-foreground mt-1 truncate">{c.name}</div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 pt-5 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Primary color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={HEX_RE.test(form.theme_color) ? form.theme_color : '#2F6B57'} onChange={(e) => set('theme_color', e.target.value)} className="h-11 w-12 rounded-lg border border-border bg-card cursor-pointer p-1" aria-label="Primary color picker" />
                <Input value={form.theme_color} onChange={(e) => set('theme_color', e.target.value)} className="h-11 font-mono text-sm" data-testid="flyer-theme-custom-theme-hex" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Accent color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={HEX_RE.test(form.accent_color) ? form.accent_color : '#C86B4A'} onChange={(e) => set('accent_color', e.target.value)} className="h-11 w-12 rounded-lg border border-border bg-card cursor-pointer p-1" aria-label="Accent color picker" />
                <Input value={form.accent_color} onChange={(e) => set('accent_color', e.target.value)} className="h-11 font-mono text-sm" data-testid="flyer-theme-custom-accent-hex" />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl font-semibold mb-1">Typography</h2>
          <p className="text-sm text-muted-foreground mb-4">Pick a font pairing for your flyer, public event page, and all invite emails. Emails fall back to a close email-safe match.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="font-theme-grid">
            {FONT_THEMES.map((t) => {
              const selected = (form.font_theme || 'editorial') === t.id;
              return (
                <button key={t.id} type="button" onClick={() => set('font_theme', t.id)}
                  className={`text-left rounded-xl border-2 p-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                  aria-pressed={selected} data-testid={`font-theme-${t.id}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xl leading-none text-foreground" style={{ fontFamily: t.display }}>{t.name}</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.desc}</span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground" style={{ fontFamily: t.body }}>
                    You&apos;re invited — join us to celebrate. 1234
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <RsvpBuilder fields={fields} onChange={setFields} />
          <div className="mt-5 pt-5 border-t border-border">
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <span className="flex items-center gap-2 text-sm font-medium"><Pencil className="h-4 w-4 text-muted-foreground" /> Let guests edit their RSVP later</span>
              <Switch checked={form.rsvp_allow_edit} onCheckedChange={(v) => set('rsvp_allow_edit', v)} data-testid="rsvp-allow-edit-toggle" />
            </label>
            <p className="text-xs text-muted-foreground mt-1.5">Guests can return to the event page and update their response using their email.</p>
          </div>
        </div>

        <Button type="submit" disabled={submitting} className="h-12 px-8 gap-2" data-testid="event-form-submit">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? 'Saving…' : submitLabel}
        </Button>
      </div>

      <div className="hidden lg:block">
        <div className="sticky top-6 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Live preview</div>
            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground" data-testid="preview-layout-badge">{(FLYER_LAYOUTS.find((l) => l.id === (form.flyer_layout || 'classic')) || {}).name}</span>
          </div>
          <div className="rounded-xl overflow-hidden border border-border">
            <div className="h-20" style={{ background: `linear-gradient(135deg, ${form.theme_color}, ${form.accent_color})` }} />
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest" style={{ color: form.accent_color }}>You&apos;re invited</div>
              <div className="text-xl font-semibold mt-1" style={{ fontFamily: getFontTheme(form.font_theme).display }}>{form.title || 'Your event title'}</div>
              <div className="text-sm text-muted-foreground mt-2 tabular-nums">{form.date ? formatDateLong(form.date) : 'Date'}{previewTime && ` · ${previewTime} ${previewAbbr}`}</div>
              <div className="text-sm text-muted-foreground">{form.location_name || 'Venue'}</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            <div className="font-medium text-foreground mb-1">RSVP collects:</div>
            <ul className="space-y-1">
              <li>· Attending (Yes / Maybe / No)</li>
              {stripFieldIds(fields).filter((f) => f.label.trim()).map((f, i) => (
                <li key={i}>· {f.label}{f.required ? ' (required)' : ''}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </form>
  );
};
